import { getStationaryBookingProgress } from '@components/BookingFlow/Common/bookingFlowProgress';
import { SelectCars } from '@components/BookingFlow/Common/Car/SelectCars';
import { StationaryBookingScreen } from '@components/BookingFlow/Stationary/StationaryBookingScreen';
import { TireHotelAtOtherDepartmentPrompt } from '@components/BookingFlow/Stationary/TireHotelAtOtherDepartmentPrompt';
import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { t } from '@lingui/core/macro';
import { BookingProposalStatus } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiBadge, usePaginationQueryParam } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { trackBookingFlowStarted, useAuthContext, useShouldFireRequestForLoggedInUser } from 'noddi-web';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBookingCarSearch } from '../../../../hooks/useBookingCarSearch';
import { useTrackStationaryBookingFlowContext } from '../../../../hooks/useTrackStationaryBookingFlowContext';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import {
  useServiceCenter,
  useStationaryBookingActions,
  useStationaryBookingCar
} from '../../../../stores/StationaryBookingStore';

export const StationaryBookingCar = () => {
  const navigate = useNavigate();

  const { currentUserGroupId: userGroupId } = useAuthContext();
  const shouldFireRequest = useShouldFireRequestForLoggedInUser();
  const { pagination } = usePaginationQueryParam();
  const { setCar, removeCar } = useStationaryBookingActions();
  const selectedCar = useStationaryBookingCar();
  const serviceCenter = useServiceCenter();
  const { currentBrand } = useCurrentBrand();
  const serviceCenterName = serviceCenter?.name;
  const carSearch = useBookingCarSearch({ addCar: setCar, selectedCars: selectedCar ? [selectedCar] : [] });
  const { canSubmit: hasTypedPlate, isSearching } = carSearch.plateSearch;
  const [advanceForPlate, setAdvanceForPlate] = useState<string>();

  useEffect(() => {
    trackBookingFlowStarted({
      brand: currentBrand,
      locationType: 'stationary',
      garageId: serviceCenter?.garageId,
      garageName: serviceCenter?.name,
      serviceDepartmentId: serviceCenter?.serviceDepartmentId
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentBrand/serviceCenter omitted: funnel-start event fires once on mount
  }, []);

  // Car and service center (incl. the tire hotel switch) only change on this
  // step, so the shared screen — also used by the pre-choice page — stays clean.
  useTrackStationaryBookingFlowContext();

  // need to use isLoading here, not isPending if not page doesn't work for not logged in users
  const {
    isLoading: isSavedCarsLoading,
    data: savedCars,
    error: savedCarsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedCarsForBookingUserGroups,
    input: { userGroupId: userGroupId as number, ...pagination },
    queryConfig: {
      enabled: shouldFireRequest,
      // Keep the current page on screen while the next loads; without this every page change
      // re-enters the loading state and the car list blanks.
      placeholderData: keepPreviousData
    }
  });

  // Counts every upcoming group booking on the car, including colleagues' bookings a plain
  // organization member cannot list, so the member cannot double-book a shared car.
  const {
    data: upcomingBookingCheck,
    isLoading: isCheckingExistingBooking,
    error: existingBookingsError
  } = noddiAsync.useGet({
    type: URLKeys.getCarUpcomingBookingCheck,
    input: {
      userGroupId: userGroupId as number,
      licensePlateNumber: selectedCar?.licensePlate.number as string,
      countryCode: selectedCar?.licensePlate.countryCode as string,
      brandDomains: [currentBrand]
    },
    queryConfig: {
      enabled: shouldFireRequest && !!selectedCar
    }
  });

  const {
    data: pendingProposals,
    isLoading: isCheckingProposal,
    error: pendingProposalsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookingProposals,
    input: {
      userGroupIds: [userGroupId as number],
      isVisibleCustomer: true,
      statuses: [BookingProposalStatus.PENDING],
      licensePlate: selectedCar?.licensePlate.number,
      pageSize: 1,
      pageIndex: 0
    },
    queryConfig: {
      enabled: shouldFireRequest && !!selectedCar
    }
  });

  const hasExistingBooking = upcomingBookingCheck?.hasUpcomingBooking ?? false;
  const hasExistingBookingProposal = (pendingProposals?.count ?? 0) > 0;
  const isBlocked = hasExistingBooking || hasExistingBookingProposal;

  const proposalBody = hasExistingBookingProposal
    ? { firstPendingProposalSlug: pendingProposals?.results[0]?.slug }
    : undefined;
  const visibleBookingSlug = upcomingBookingCheck?.visibleBookingSlug ?? undefined;
  const existingBookingBody = hasExistingBooking ? { firstUpcomingBookingSlug: visibleBookingSlug } : undefined;
  // No visible slug means only colleagues' bookings block the car, which this member cannot open or cancel.
  const existingBookingVariant = visibleBookingSlug ? 'existingBooking' : 'existingColleagueBooking';

  const validationVariant = hasExistingBookingProposal
    ? 'existingBookingProposal'
    : hasExistingBooking
      ? existingBookingVariant
      : 'carSelectionRequired';
  const validationBody = hasExistingBookingProposal ? proposalBody : existingBookingBody;

  const isChecking = isCheckingExistingBooking || isCheckingProposal;
  const checkError = existingBookingsError ?? pendingProposalsError;

  // Next submits a plate typed but not added. Its existing-booking checks only start once the car
  // is in the store, so advance when they settle for that car; a blocked car stays for the modal.
  const isAdvancePending = !!selectedCar && selectedCar.licensePlate.number === advanceForPlate;
  useEffect(() => {
    if (isAdvancePending && !isChecking && !isBlocked && !checkError) {
      navigate(customerRoutes.newStationaryBookingServices.getPath());
    }
  }, [isAdvancePending, isChecking, isBlocked, checkError, navigate]);

  async function onNavigateToNextStep() {
    if (hasTypedPlate) {
      setAdvanceForPlate((await carSearch.submitTypedPlate())?.licensePlate.number);
      return;
    }
    navigate(customerRoutes.newStationaryBookingServices.getPath());
  }

  return (
    <StationaryBookingScreen
      progress={getStationaryBookingProgress('car')}
      // oxlint-disable-next-line typescript/no-misused-promises -- the plate lookup's refetch never rejects; a failed lookup renders from the query's error
      onNextClicked={onNavigateToNextStep}
      disableNextButton={!hasTypedPlate && (!selectedCar || isBlocked)}
      nextButtonLoading={isChecking || isSearching}
      title={t`Choose car`}
      description={t`Only one car can be booked at a time`}
      validationVariant={validationVariant}
      validationBody={validationBody}
    >
      {savedCarsError || existingBookingsError || pendingProposalsError ? (
        <ApiErrorMessageWithTrans error={savedCarsError ?? existingBookingsError ?? pendingProposalsError!} />
      ) : (
        <div className='flex flex-col gap-4'>
          {serviceCenterName && (
            <NoddiBadge variant='info' className='h-fit'>
              <NoddiIcon name='MapPin' size='xSmall' />
              {serviceCenterName}
            </NoddiBadge>
          )}
          <SelectCars
            loading={isSavedCarsLoading}
            savedCars={savedCars?.results}
            carSearch={carSearch}
            removeCar={removeCar}
            selectedCars={selectedCar ? [selectedCar] : []}
            serviceDepartmentId={serviceCenter?.serviceDepartmentId}
          />
          <TireHotelAtOtherDepartmentPrompt />
        </div>
      )}
    </StationaryBookingScreen>
  );
};
